/**
 * GIT CONFIG STORE
 *
 * Per-workspace Git identity (user.name / user.email) for local commits.
 * Persisted to localStorage so it survives page reloads.
 */

import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface GitConfig {
  userName: string;
  userEmail: string;
}

interface GitConfigState {
  configs: Record<string, GitConfig>; // key = workspace string ID
  getConfig: (workspaceId: string) => GitConfig | null;
  setConfig: (workspaceId: string, config: GitConfig) => void;
  clearConfig: (workspaceId: string) => void;
}

export const useGitConfigStore = create<GitConfigState>()(
  persist(
    (set, get) => ({
      configs: {},

      getConfig: (workspaceId: string) => {
        const cfg = get().configs[workspaceId];
        if (!cfg) return null;
        // Both must be non-empty to be valid
        if (cfg.userName.trim() && cfg.userEmail.trim()) return cfg;
        return null;
      },

      setConfig: (workspaceId: string, config: GitConfig) => {
        set((s) => ({
          configs: { ...s.configs, [workspaceId]: config },
        }));
      },

      clearConfig: (workspaceId: string) => {
        set((s) => {
          const next = { ...s.configs };
          delete next[workspaceId];
          return { configs: next };
        });
      },
    }),
    { name: "factory-git-config", version: 1 }
  )
);
